import { AlertTriangle, Trash2, Info } from 'lucide-react';
import { palette, transition } from '../../styles/tokens';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  confirmVariant?: 'danger' | 'primary' | 'warning';
  loading?: boolean;
}

export function ConfirmDialog({ isOpen, onClose, onConfirm, title, message, confirmLabel = 'Confirm', confirmVariant = 'danger', loading }: ConfirmDialogProps) {
  if (!isOpen) return null;

  const iconConfig = confirmVariant === 'danger'
    ? { Icon: Trash2, bg: palette.redWash, fg: palette.red }
    : confirmVariant === 'warning'
    ? { Icon: AlertTriangle, bg: palette.amberWash, fg: palette.amber }
    : { Icon: Info, bg: palette.blueWash, fg: palette.blue };

  const btnBg = confirmVariant === 'danger' ? palette.red : confirmVariant === 'warning' ? palette.amber : palette.accent;

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 380, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
        animation: 'confirmIn 200ms ease-out',
      }}>
        <style>{`@keyframes confirmIn { from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }`}</style>

        {/* Icon */}
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: iconConfig.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 16,
        }}>
          <iconConfig.Icon size={22} color={iconConfig.fg} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: 24 }}>{message}</div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}
            onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
            onMouseLeave={e => (e.currentTarget.style.background = palette.white)}
          >Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: loading ? palette.g300 : btnBg, color: palette.white,
            fontSize: 13.5, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>{loading ? 'Processing...' : confirmLabel}</button>
        </div>
      </div>
    </>
  );
}
